import React, { useContext, useEffect, useMemo, useRef, useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Button, Select, Input, Modal, message, Space, Tag, Skeleton, Divider, Empty, Tooltip, Alert, Grid } from "antd"
import dayjs, { Dayjs } from "dayjs"
import { ContainerFull } from "../components"
import { UserContext } from "../context"
import { isAdmin as checkIsAdmin } from "../utils/userUtils"
import {
    getOrderPlanning,
    getOrderPlanningHistory,
    getOrderPlanningHistoryRange,
    createOrderPlanningEntries,
    getUserGroups,
    OrderPlanningEntry,
    OrderPlanningStatus,
} from "../endpoints"

const { TextArea } = Input

const DEFAULT_ADMIN_GROUP_NAME = "waybio"

const STATUS_OPTIONS: { value: OrderPlanningStatus; label: string; color: string; textColor: string }[] = [
    { value: "yes", label: "Yes", color: "#d9f7be", textColor: "#237804" },
    { value: "maybe", label: "Maybe", color: "#fff1b8", textColor: "#874d00" },
    { value: "no", label: "No", color: "#f0f0f0", textColor: "#434343" },
    { value: "unavailable", label: "Unavailable", color: "#fba4a4", textColor: "#434343" },
]

function statusMeta(status?: string | null) {
    return STATUS_OPTIONS.find((s) => s.value === status)
}

// week starts on Monday
function getWeekStart(d: Dayjs): Dayjs {
    const dow = d.day() // 0 = Sun ... 6 = Sat
    const diff = (dow + 6) % 7
    return d.subtract(diff, "day").startOf("day")
}

export const OrderPlanning = (): JSX.Element => {
    const queryClient = useQueryClient()
    const userContext = useContext(UserContext)
    const admin = checkIsAdmin(userContext.user)
    // phones get single-line notes in calendar cells; larger screens keep the note's line breaks
    const isSmallScreen = !Grid.useBreakpoint().md

    const visibleStatusOptions = useMemo(
        () => STATUS_OPTIONS.filter((s) => admin || s.value !== "unavailable"),
        [admin]
    )

    // admins can browse/edit any group's calendar; everyone else is locked to their own group
    const groupsQuery = useQuery({
        queryKey: ["userGroupsForOrderPlanning"],
        queryFn: () => getUserGroups(),
        enabled: admin,
    })

    const [selectedGroupId, setSelectedGroupId] = useState<number | undefined>(undefined)
    useEffect(() => {
        if (admin && selectedGroupId === undefined && groupsQuery.data?.groups?.length) {
            const defaultGroup = groupsQuery.data.groups.find(
                (g) => g.name?.toLowerCase() === DEFAULT_ADMIN_GROUP_NAME
            )
            setSelectedGroupId(defaultGroup?.id ?? groupsQuery.data.groups[0].id)
        }
    }, [admin, groupsQuery.data, selectedGroupId])

    const effectiveGroupId = admin ? selectedGroupId : undefined

    // number of 4-week blocks offset from the block containing today; lets users page back/forth
    const [weekOffset, setWeekOffset] = useState(0)

    const days = useMemo(() => {
        const start = getWeekStart(dayjs()).add(weekOffset * 28, "day")
        return Array.from({ length: 28 }, (_, i) => start.add(i, "day"))
    }, [weekOffset])
    const dateIndex = useMemo(() => {
        const map: Record<string, number> = {}
        days.forEach((d, i) => (map[d.format("YYYY-MM-DD")] = i))
        return map
    }, [days])
    const weeks = useMemo(() => {
        const chunks: Dayjs[][] = []
        for (let i = 0; i < days.length; i += 7) chunks.push(days.slice(i, i + 7))
        return chunks
    }, [days])

    const startDate = days[0].format("YYYY-MM-DD")
    const endDate = days[days.length - 1].format("YYYY-MM-DD")

    const { isPending, isError, data, error } = useQuery({
        queryKey: ["orderPlanning", startDate, endDate, effectiveGroupId],
        queryFn: () => getOrderPlanning({ startDate, endDate, groupid: effectiveGroupId }),
        enabled: !admin || selectedGroupId !== undefined,
        refetchOnMount: "always",
    })

    const entriesByDate = useMemo(() => {
        const map: Record<string, OrderPlanningEntry> = {}
        data?.entries?.forEach((e) => {
            map[dayjs(e.date).format("YYYY-MM-DD")] = e
        })
        return map
    }, [data])

    function isLocked(dateStr: string) {
        return !admin && entriesByDate[dateStr]?.status === "unavailable"
    }

    const [selectedDates, setSelectedDates] = useState<string[]>([])
    const [anchorIndex, setAnchorIndex] = useState<number | null>(null)
    const [pendingStatus, setPendingStatus] = useState<OrderPlanningStatus | null>(null)
    // selection as it was before the latest single click, so a double-click can undo that click's toggle
    const selectionBeforeClickRef = useRef<{ dates: string[]; anchor: number | null } | null>(null)

    const [modalDate, setModalDate] = useState<string | null>(null)
    const [modalStatus, setModalStatus] = useState<OrderPlanningStatus | undefined>(undefined)
    const [modalNotes, setModalNotes] = useState("")

    const saveMutation = useMutation({
        mutationFn: createOrderPlanningEntries,
        onError: (e: any) => {
            message.error(e?.message || "Failed to save")
        },
    })

    const historyQuery = useQuery({
        queryKey: ["orderPlanningHistory", modalDate, effectiveGroupId],
        queryFn: () => getOrderPlanningHistory({ date: modalDate as string, groupid: effectiveGroupId }),
        enabled: !!modalDate,
    })

    const historyRangeQuery = useQuery({
        queryKey: ["orderPlanningHistoryRange", startDate, endDate, effectiveGroupId],
        queryFn: () => getOrderPlanningHistoryRange({ startDate, endDate, groupid: effectiveGroupId }),
        enabled: !admin || selectedGroupId !== undefined,
    })

    // Group history entries by (user, timestamp rounded to the minute) so a single save
    // that touched multiple days shows as one row, then sort those rows newest-first.
    const historyRangeGrouped = useMemo(() => {
        const groups = new Map<string, {
            ts: string
            firstName?: string | null
            lastName?: string | null
            items: { date: string; status: OrderPlanningEntry["status"]; notes: string | null }[]
        }>()
        historyRangeQuery.data?.history?.forEach((h) => {
            const minuteKey = dayjs(h.ts).format("YYYY-MM-DD HH:mm")
            const key = `${minuteKey}__${h.userid ?? "unknown"}`
            let group = groups.get(key)
            if (!group) {
                group = { ts: h.ts, firstName: h.firstName, lastName: h.lastName, items: [] }
                groups.set(key, group)
            }
            group.items.push({ date: h.date, status: h.status, notes: h.notes })
        })
        return Array.from(groups.values())
            .map((g) => {
                const items = [...g.items].sort((a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf())
                // Bucket the dates by status (in order of each status's first appearance) so
                // e.g. dates set to No, Yes, No collapse to "<no-dates> to No, <yes-dates> to Yes".
                const statusGroups: { status: OrderPlanningEntry["status"]; entries: { date: string; notes: string | null }[] }[] = []
                const indexByStatus = new Map<OrderPlanningEntry["status"], number>()
                items.forEach((item) => {
                    let idx = indexByStatus.get(item.status)
                    if (idx === undefined) {
                        idx = statusGroups.length
                        indexByStatus.set(item.status, idx)
                        statusGroups.push({ status: item.status, entries: [] })
                    }
                    statusGroups[idx].entries.push({ date: item.date, notes: item.notes })
                })
                return { ...g, items, statusGroups }
            })
            .sort((a, b) => dayjs(b.ts).valueOf() - dayjs(a.ts).valueOf())
    }, [historyRangeQuery.data])

    function goToPreviousPeriod() {
        setWeekOffset((o) => o - 1)
        clearSelection()
    }
    function goToNextPeriod() {
        setWeekOffset((o) => o + 1)
        clearSelection()
    }
    function goToToday() {
        setWeekOffset(0)
        clearSelection()
    }

    function onGroupChange(groupId: number) {
        setSelectedGroupId(groupId)
        setSelectedDates([])
        setAnchorIndex(null)
        setPendingStatus(null)
    }

    function handleDayClick(dateStr: string, index: number, e: React.MouseEvent) {
        // the second click of a double-click is handled by handleDayDoubleClick
        if (e.detail > 1) return
        selectionBeforeClickRef.current = { dates: selectedDates, anchor: anchorIndex }
        if (isLocked(dateStr)) return
        const shiftKey = e.shiftKey
        if (shiftKey && anchorIndex !== null) {
            const [start, end] = anchorIndex < index ? [anchorIndex, index] : [index, anchorIndex]
            const range = days
                .slice(start, end + 1)
                .map((d) => d.format("YYYY-MM-DD"))
                .filter((d) => !isLocked(d))
            setSelectedDates(range)
        } else {
            setSelectedDates((prev) =>
                prev.includes(dateStr) ? prev.filter((d) => d !== dateStr) : [...prev, dateStr]
            )
            setAnchorIndex(index)
        }
    }

    function handleDayDoubleClick(dateStr: string) {
        // a double-click means "open details", so revert the selection change made by its first click
        if (selectionBeforeClickRef.current) {
            setSelectedDates(selectionBeforeClickRef.current.dates)
            setAnchorIndex(selectionBeforeClickRef.current.anchor)
            selectionBeforeClickRef.current = null
        }
        openModal(dateStr)
    }

    function clearSelection() {
        setSelectedDates([])
        setAnchorIndex(null)
        setPendingStatus(null)
    }

    function openModal(dateStr: string) {
        const existing = entriesByDate[dateStr]
        setModalDate(dateStr)
        setModalStatus((existing?.status as OrderPlanningStatus) || undefined)
        setModalNotes(existing?.notes || "")
    }

    function closeModal() {
        setModalDate(null)
        setModalStatus(undefined)
        setModalNotes("")
    }

    function saveChanges() {
        if (selectedDates.length === 0) {
            message.warning("Please select at least one day")
            return
        }
        if (!pendingStatus) {
            message.warning("Please choose a status to apply")
            return
        }
        saveMutation.mutate(
            { dates: selectedDates, status: pendingStatus, groupid: effectiveGroupId },
            {
                onSuccess: (result) => {
                    if (result.code === 1000) {
                        message.success("Saved")
                        clearSelection()
                        queryClient.invalidateQueries({ queryKey: ["orderPlanning"] })
                    } else {
                        message.error(result.message || "Failed to save")
                    }
                },
            }
        )
    }

    const modalLocked = !!(modalDate && !admin && entriesByDate[modalDate]?.status === "unavailable")

    function saveModal() {
        if (!modalDate) return
        if (!modalStatus) {
            message.warning("Please choose a status")
            return
        }
        const savedDate = modalDate
        saveMutation.mutate(
            { dates: [savedDate], status: modalStatus, notes: modalNotes, groupid: effectiveGroupId },
            {
                onSuccess: (result) => {
                    if (result.code === 1000) {
                        message.success("Saved")
                        queryClient.invalidateQueries({ queryKey: ["orderPlanning"] })
                        queryClient.invalidateQueries({ queryKey: ["orderPlanningHistory", savedDate] })
                        closeModal()
                    } else {
                        message.error(result.message || "Failed to save")
                    }
                },
            }
        )
    }

    if (isPending)
        return (
            <ContainerFull>
                <Skeleton />
            </ContainerFull>
        )
    if (isError || data.code !== 1000)
        return (
            <ContainerFull>
                <div>Error: {(error as any)?.message || data?.message || "Unknown error"}</div>
            </ContainerFull>
        )

    const todayStr = dayjs().format("YYYY-MM-DD")
    const weekDayLabels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]

    return (
        <ContainerFull style={{ fontFamily: '"Rubik", sans-serif' }}>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 16,
                    flexWrap: "wrap",
                    gap: 8,
                }}
            >
                <h2 style={{ margin: 0 }}>Order Planning</h2>
                <Space wrap>
                    {visibleStatusOptions.map((s) => (
                        <Tag key={s.value} style={{ background: s.color, color: s.textColor, border: "none" }}>
                            {s.label}
                        </Tag>
                    ))}
                </Space>
            </div>

            {admin && (
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                    <span style={{ color: "#333", fontWeight: 600 }}>Group:</span>
                    <Select
                        style={{ width: 260 }}
                        value={selectedGroupId}
                        loading={groupsQuery.isPending}
                        onChange={onGroupChange}
                        options={groupsQuery.data?.groups?.map((g) => ({ value: g.id, label: g.name })) || []}
                    />
                </div>
            )}

            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    gap: 8,
                    marginBottom: 12,
                }}
            >
                <Space>
                    <Button onClick={goToPreviousPeriod}>◀ Previous</Button>
                    <Button onClick={goToToday} disabled={weekOffset === 0}>
                        Today
                    </Button>
                    <Button onClick={goToNextPeriod}>Next ▶</Button>
                    <span style={{ color: "#333", fontWeight: 600 }}>
                        {days[0].format("MMM D")} - {days[days.length - 1].format("MMM D, YYYY")}
                    </span>
                </Space>
            </div>

            <div style={{ color: "#888", marginBottom: 12, fontSize: 13 }}>
                Click a day to select it (shift-click to select a range), then choose a status below to stage it, and
                click "Save Changes" to apply. Double-click a day to view details, edit notes, and see its history.
            </div>

            {/* always rendered (disabled when empty) so the calendar never shifts under the cursor between clicks */}
            <div
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    flexWrap: "wrap",
                    marginBottom: 16,
                    padding: 12,
                    background: "#fafafa",
                    border: "1px solid #eee",
                    borderRadius: 6,
                }}
            >
                <span style={{ color: selectedDates.length > 0 ? "#222" : "#999" }}>
                    {selectedDates.length} day{selectedDates.length === 1 ? "" : "s"} selected
                </span>
                <Space wrap>
                    {visibleStatusOptions.map((s) => (
                        <Button
                            key={s.value}
                            disabled={selectedDates.length === 0}
                            onClick={() => setPendingStatus(s.value)}
                            style={{
                                background: s.color,
                                color: s.textColor,
                                border: pendingStatus === s.value ? `2px solid ${s.textColor}` : "1px solid transparent",
                                fontWeight: 600,
                                opacity: selectedDates.length === 0 ? 0.5 : 1,
                            }}
                        >
                            {s.label}
                        </Button>
                    ))}
                </Space>
                <Button
                    type="primary"
                    disabled={selectedDates.length === 0}
                    loading={saveMutation.isPending}
                    onClick={saveChanges}
                >
                    Save Changes
                </Button>
                <Button disabled={selectedDates.length === 0} onClick={clearSelection}>
                    Clear selection
                </Button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 4, marginBottom: 4 }}>
                {weekDayLabels.map((label) => (
                    <div key={label} style={{ textAlign: "center", fontWeight: 600, color: "#888", fontSize: 12 }}>
                        {label}
                    </div>
                ))}
            </div>

            {weeks.map((week, wi) => (
                <div key={wi} style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 4, marginBottom: 4 }}>
                    {week.map((day) => {
                        const dateStr = day.format("YYYY-MM-DD")
                        const entry = entriesByDate[dateStr]
                        const meta = statusMeta(entry?.status)
                        const isSelected = selectedDates.includes(dateStr)
                        const isToday = dateStr === todayStr
                        const locked = isLocked(dateStr)
                        const stagedMeta = isSelected && pendingStatus ? statusMeta(pendingStatus) : undefined
                        return (
                            <div
                                key={dateStr}
                                onClick={(e) => handleDayClick(dateStr, dateIndex[dateStr], e)}
                                onDoubleClick={() => handleDayDoubleClick(dateStr)}
                                title={locked ? "Locked by admin" : "Click to select, double-click to edit"}
                                style={{
                                    cursor: locked ? "not-allowed" : "pointer",
                                    minHeight: 84,
                                    minWidth: 0,
                                    overflow: "hidden",
                                    padding: 6,
                                    borderRadius: 6,
                                    background: stagedMeta ? stagedMeta.color : meta ? meta.color : "#fff",
                                    opacity: locked ? 0.85 : 1,
                                    border: isSelected
                                        ? "2px solid #1677ff"
                                        : isToday
                                        ? "2px solid #faad14"
                                        : "1px solid #ddd",
                                    boxSizing: "border-box",
                                    userSelect: "none",
                                    display: "flex",
                                    flexDirection: "column",
                                }}
                            >
                                <div style={{ fontSize: 12, color: "#888" }}>{day.format("MMM D")}</div>
                                <div
                                    style={{
                                        flex: 1,
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: entry?.notes ? "flex-start" : "center",
                                    }}
                                >
                                    {stagedMeta ? (
                                        <div style={{ fontSize: 12, fontWeight: 600, color: stagedMeta.textColor }}>
                                            {stagedMeta.label} (pending)
                                        </div>
                                    ) : meta ? (
                                        <div style={{ fontSize: 12, fontWeight: 600, color: meta.textColor }}>{meta.label}</div>
                                    ) : (
                                        <div style={{ fontSize: 12, color: "#bbb" }}></div>
                                    )}
                                    {entry?.notes && (
                                        <Tooltip title={entry.notes}>
                                            <div
                                                style={{
                                                    fontSize: 11,
                                                    color: "#666",
                                                    overflow: "hidden",
                                                    marginTop: 2,
                                                    ...(isSmallScreen
                                                        ? { textOverflow: "ellipsis", whiteSpace: "nowrap" }
                                                        : {
                                                              whiteSpace: "pre-line",
                                                              overflowWrap: "anywhere",
                                                              display: "-webkit-box",
                                                              WebkitLineClamp: 3,
                                                              WebkitBoxOrient: "vertical",
                                                          }),
                                                }}
                                            >
                                                {entry.notes}
                                            </div>
                                        </Tooltip>
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            ))}

            <div style={{ marginTop: 24 }}>
                <span style={{ margin: "0 0 12px" }}>
                    History: {days[0].format("MMM D")} - {days[days.length - 1].format("MMM D, YYYY")}
                </span>
                {historyRangeQuery.isPending ? (
                    <Skeleton active paragraph={{ rows: 4 }} />
                ) : historyRangeGrouped.length > 0 ? (
                    <div style={{ maxHeight: 420, overflowY: "auto", border: "1px solid #eee", borderRadius: 6, padding: 12 }}>
                        {historyRangeGrouped.map((g) => (
                            <div key={`${g.ts}__${g.firstName}__${g.lastName}`} style={{ padding: "6px 0", borderBottom: "1px solid #f0f0f0" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                    <span>
                                        {g.firstName || "Unknown user"} set{" "}
                                        {g.statusGroups.map((grp, gi) => {
                                            const grpMeta = statusMeta(grp.status)
                                            return (
                                                <span key={String(grp.status)}>
                                                    {gi > 0 && ", "}
                                                    {grp.entries.map((entry, ei) => (
                                                        <span key={entry.date}>
                                                            {ei > 0 && ", "}
                                                            {dayjs(entry.date).format("MM/DD/YYYY")}
                                                            {entry.notes && (
                                                                <span style={{ color: "#666", fontSize: 13 }}> ({entry.notes.replace(/\r?\n/g, "\\n ")})</span>
                                                            )}
                                                        </span>
                                                    ))}
                                                    {" to "}
                                                    {grpMeta ? grpMeta.label : grp.status}
                                                </span>
                                            )
                                        })}
                                    </span>
                                    <span style={{ color: "#999", fontSize: 12, whiteSpace: "nowrap" }}>
                                        {dayjs(g.ts).format("MMM D, YYYY h:mm A")}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <Empty description="No history in this view" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
            </div>

            <Modal
                title={modalDate ? dayjs(modalDate).format("dddd, MMMM D, YYYY") : ""}
                open={!!modalDate}
                onCancel={closeModal}
                onOk={saveModal}
                okText="Save"
                okButtonProps={{ disabled: modalLocked }}
                confirmLoading={saveMutation.isPending}
                destroyOnClose
            >
                {modalLocked && (
                    <Alert
                        type="warning"
                        showIcon
                        message="This day is marked unavailable and can only be changed by an admin."
                        style={{ marginBottom: 12 }}
                    />
                )}
                <div style={{ marginBottom: 12 }}>
                    <div style={{ marginBottom: 4, color: "#333", fontWeight: 600 }}>Status</div>
                    <Space wrap>
                        {STATUS_OPTIONS.filter((s) => admin || s.value !== "unavailable").map((s) => (
                            <Button
                                key={s.value}
                                disabled={modalLocked}
                                onClick={() => setModalStatus(s.value)}
                                style={{
                                    background: s.color,
                                    color: s.textColor,
                                    border: modalStatus === s.value ? `2px solid ${s.textColor}` : "1px solid transparent",
                                    fontWeight: 600,
                                }}
                            >
                                {s.label}
                            </Button>
                        ))}
                    </Space>
                </div>
                <div style={{ marginBottom: 12 }}>
                    <div style={{ marginBottom: 4, color: "#333", fontWeight: 600 }}>Notes</div>
                    <TextArea
                        rows={3}
                        value={modalNotes}
                        onChange={(e) => setModalNotes(e.target.value)}
                        placeholder="Add notes..."
                        disabled={modalLocked}
                    />
                </div>

                <Divider orientation="left" plain>
                    History
                </Divider>
                {historyQuery.isPending ? (
                    <Skeleton active paragraph={{ rows: 2 }} />
                ) : historyQuery.data?.history && historyQuery.data.history.length > 0 ? (
                    <div style={{ maxHeight: 220, overflowY: "auto" }}>
                        {historyQuery.data.history.map((h) => {
                            const hMeta = statusMeta(h.status)
                            return (
                                <div key={h.id} style={{ padding: "6px 0", borderBottom: "1px solid #f0f0f0" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                        <span>
                                            {hMeta && (
                                                <Tag style={{ background: hMeta.color, color: hMeta.textColor, border: "none" }}>
                                                    {hMeta.label}
                                                </Tag>
                                            )}
                                            {h.firstName || "Unknown user"}
                                        </span>
                                        <span style={{ color: "#999", fontSize: 12, whiteSpace: "nowrap" }}>
                                            {dayjs(h.ts).format("MMM D, YYYY h:mm A")}
                                        </span>
                                    </div>
                                    {h.notes && <div style={{ color: "#666", fontSize: 13, whiteSpace: "pre-wrap" }}>{h.notes}</div>}
                                </div>
                            )
                        })}
                    </div>
                ) : (
                    <Empty description="No history yet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
            </Modal>
        </ContainerFull>
    )
}
